import { useState, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

// Step field mappings for validation
const STEP_FIELDS = {
  1: ['first_name', 'last_name', 'email', 'email_verification', 'phone'],
  2: 'passengers', // Form.List - validated separately
  3: [], // Baggage is optional, no required fields
  4: [], // Order summary is read-only
  5: ['check1', 'check2'] // Required checkboxes
};

const STEP_NAMES = {
  1: 'booker_details',
  2: 'passenger_details',
  3: 'baggage_selection',
  4: 'order_summary',
  5: 'payment_approval'
};

// Step titles are now dynamic and come from translations

/**
 * Check if we're on desktop
 */
const isDesktop = () => typeof window !== 'undefined' && window.innerWidth > 768;

/**
 * Custom hook for managing booking wizard state and navigation
 * On desktop, Step 4 (summary) is skipped since sidebar shows summary
 */
const useBookingWizard = (form, quoteUid) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(1);
  const [completedSteps, setCompletedSteps] = useState(new Set());
  const [stepErrors, setStepErrors] = useState({});

  /**
   * Get the next step
   */
  const getNextStep = useCallback((step) => {
    return step + 1;
  }, []);

  /**
   * Get the status of a step
   */
  const getStepStatus = useCallback((step) => {
    // IMPORTANT: Check currentStep FIRST - allows reopening completed steps for editing
    if (step === currentStep) return 'active';
    if (completedSteps.has(step)) return 'completed';
    return 'pending';
  }, [currentStep, completedSteps]);

  /**
   * Check if a step is accessible
   */
  const isStepAccessible = useCallback((step) => {
    if (completedSteps.has(step)) return true;
    if (step === currentStep) return true;
    return false;
  }, [currentStep, completedSteps]);

  /**
   * Validate a specific step's fields
   */
  const validateStep = useCallback(async (step) => {
    if (!form) return false;

    const fields = STEP_FIELDS[step];

    // Step 3 and 4 have no required fields
    if (!fields || fields.length === 0) {
      return true;
    }

    // Step 2: Validate passengers Form.List
    if (step === 2 && fields === 'passengers') {
      try {
        const values = form.getFieldsValue();
        const passengers = values.passengers || [];

        const passengerFields = [];
        passengers.forEach((_, index) => {
          passengerFields.push(
            ['passengers', index, 'first_name'],
            ['passengers', index, 'last_name'],
            ['passengers', index, 'gender'],
            ['passengers', index, 'birth_year'],
            ['passengers', index, 'birth_month'],
            ['passengers', index, 'birth_day']
          );
        });

        await form.validateFields(passengerFields);
        setStepErrors(prev => ({ ...prev, [step]: null }));
        return true;
      } catch (error) {
        setStepErrors(prev => ({ ...prev, [step]: error.errorFields }));
        return false;
      }
    }

    // Step 5: Validate checkboxes
    if (step === 5) {
      try {
        await form.validateFields(fields);
        setStepErrors(prev => ({ ...prev, [step]: null }));
        return true;
      } catch (error) {
        setStepErrors(prev => ({ ...prev, [step]: error.errorFields }));
        return false;
      }
    }

    // Standard field validation
    try {
      await form.validateFields(fields);
      setStepErrors(prev => ({ ...prev, [step]: null }));
      return true;
    } catch (error) {
      setStepErrors(prev => ({ ...prev, [step]: error.errorFields }));
      return false;
    }
  }, [form]);

  /**
   * Complete current step and move to next
   */
  const completeStep = useCallback(async (step) => {
    const isValid = await validateStep(step);

    if (!isValid) {
      return false;
    }

    // Mark step as completed
    setCompletedSteps(prev => new Set([...prev, step]));

    // Move to next step
    if (step < 5) {
      const nextStep = getNextStep(step);
      setCurrentStep(nextStep);
    }

    // Fire GTM event
    if (window.dataLayer) {
        
        console.log('GTM event: ' + 'booking_step_'+step+'_complete')

        //window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'booking_step_'+step+'_complete',
            step_number: step,
            step_name: STEP_NAMES[step],
            quote_uid: quoteUid,
            pageUrl: window.location.href
        });
    }

    return true;
  }, [validateStep, quoteUid, getNextStep]);

  /**
   * Navigate to a specific step
   */
  const goToStep = useCallback((step) => {
    console.log('goToStep called:', step, 'accessible:', isStepAccessible(step), 'completedSteps:', Array.from(completedSteps));
    if (isStepAccessible(step)) {
      setCurrentStep(step);
    }
  }, [isStepAccessible, completedSteps]);

  /**
   * Generate summary text for a completed step
   */
  const getSummaryForStep = useCallback((step, formValues, baggageState = []) => {
    if (!formValues) return '';

    switch (step) {
      case 1: {
        const firstName = formValues.first_name || '';
        const lastName = formValues.last_name || '';
        const email = formValues.email || '';
        if (firstName && lastName && email) {
          return `${firstName} ${lastName} | ${email}`;
        }
        return '';
      }

      case 2: {
        const passengers = formValues.passengers || [];
        const count = passengers.length;
        if (count === 0) return '';

        const names = passengers
          .filter(p => p && p.first_name && p.last_name)
          .map(p => `${p.first_name} ${p.last_name}`)
          .join(', ');

        return t('booking.summary.passengers', { count, names });
      }

      case 3: {
        const paidItems = baggageState.filter(b => b.baggage?.price > 0);
        if (paidItems.length === 0) return t('booking.summary.handBaggageOnly');

        const totalPrice = paidItems.reduce((sum, item) => sum + (item.baggage?.price || 0), 0);
        return t('booking.summary.baggageItems', { count: paidItems.length, totalPrice });
      }

      case 4:
        return t('booking.summary.orderReviewed');

      case 5:
        return t('booking.summary.readyForPayment');

      default:
        return '';
    }
  }, []);

  /**
   * Reset wizard to initial state
   */
  const resetWizard = useCallback(() => {
    setCurrentStep(1);
    setCompletedSteps(new Set());
    setStepErrors({});
  }, []);

  /**
   * Restore wizard state (for localStorage persistence)
   */
  const restoreState = useCallback((savedState) => {
    if (savedState) {
      if (savedState.currentStep) {
        setCurrentStep(savedState.currentStep);
      }
      if (savedState.completedSteps) {
        setCompletedSteps(new Set(savedState.completedSteps));
      }
    }
  }, []);

  /**
   * Get current state for persistence
   */
  const getStateForPersistence = useCallback(() => {
    return {
      currentStep,
      completedSteps: Array.from(completedSteps)
    };
  }, [currentStep, completedSteps]);

  // Memoized step info - titles are now dynamic from translations
  const stepInfo = useMemo(() => ({
    titles: {
      1: t('reservation.steps.bookerDetails.title'),
      2: t('reservation.steps.passengerDetails.title'),
      3: t('reservation.steps.baggageSelection.title'),
      4: t('reservation.steps.orderSummary.title'),
      5: t('reservation.steps.paymentApproval.title')
    },
    names: STEP_NAMES,
    total: 5
  }), [t]);

  return {
    currentStep,
    completedSteps,
    stepErrors,
    stepInfo,
    getStepStatus,
    isStepAccessible,
    validateStep,
    completeStep,
    goToStep,
    resetWizard,
    restoreState,
    getStateForPersistence,
    getSummaryForStep
  };
};

export default useBookingWizard;
